import { SortDir } from '__generated__';
import {
  Menu,
  MenuItem,
  MenuList,
  SvgIcon,
  Typography,
  TypographyProps,
} from '@mui/material';
import classNames from 'classnames';
import bind from 'memoize-bind';
import React, { PureComponent } from 'react';

import { FeatureFlags } from '@sbu/views.featureflags';
import {
  Button,
  ButtonProps,
  ListItemText,
} from '@sumologic/yama-ds-components';
import { AngleDownIcon, SortIcon } from '@sumologic/yama-ds-icons';

import {
  Sort,
  SortDirection,
  SortDirectionMap,
  SortDirType,
  SortHeader,
} from 'shared/components/data/Sort';

import css from './SortMenu.module.scss';

export interface SortOptionType<T = string> {
  [name: string]: {
    label: string;
    name: T;
    defaultDirection?: SortDirType;
    isEnabled?: (featureFlags: string[]) => boolean;
  };
}

interface SortMenuProps<T = string> {
  buttonProps?: Partial<ButtonProps>;
  buttonTextProps?: TypographyProps;
  className: string;
  dataSumoTestPrefix: string;
  menuStyleProps?: any;
  showEndIcon?: boolean;
  sort: T;
  sortDir: SortDirType;
  sortOptions: SortOptionType<T>;
  onSortChange?: (sort: T) => void;
  onSortDirChange?: (sortDir: SortDirType) => void;
  onSortsChange?: (sorts: SortDirection[]) => void;
}

interface SortMenuState<T> {
  sortMenuAnchorEl: HTMLElement | null;
  sorts: SortDirection<T>[];
}

export class SortMenu<T = string> extends PureComponent<
  SortMenuProps<T>,
  SortMenuState<T>
> {
  static defaultProps: Partial<SortMenuProps> = {
    buttonTextProps: {},
    showEndIcon: true,
  };

  state: SortMenuState<T> = {
    sortMenuAnchorEl: null,
    sorts: [
      {
        prop: this.props.sort,
        direction: this.props.sortDir,
      },
    ],
  };

  componentDidUpdate(prevProps: SortMenuProps<T>) {
    const { sort, sortDir } = this.props;
    const sortsChanged =
      prevProps.sort !== sort || prevProps.sortDir !== sortDir;

    if (sortsChanged) {
      this.setState({ sorts: [{ prop: sort, direction: sortDir }] });
    }
  }

  toggleSortMenu(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    this.setState({ sortMenuAnchorEl: e.currentTarget });
  }

  onCloseSortMenu() {
    this.setState({ sortMenuAnchorEl: null });
  }

  onSortChange(sorts: SortDirection<T>[]) {
    const { sort, sortDir, onSortChange, onSortDirChange, onSortsChange } =
      this.props;
    const [newSort] = sorts;

    if (onSortChange && onSortDirChange) {
      if (sort !== newSort.prop) {
        onSortChange(newSort.prop);
      }

      const { direction } = newSort;
      if (sortDir !== direction) {
        onSortDirChange(direction || SortDir.Asc);
      }
    } else if (onSortsChange) {
      onSortsChange(sorts);
    }

    this.setState({ sortMenuAnchorEl: null });
  }

  render() {
    const {
      buttonProps,
      buttonTextProps,
      className,
      dataSumoTestPrefix,
      menuStyleProps,
      showEndIcon,
      sortOptions,
    } = this.props;
    const { sortMenuAnchorEl, sorts } = this.state;
    const sortMenuVisible = Boolean(sortMenuAnchorEl);
    const [current] = sorts;
    const currentProp = current.prop as any as string;

    const defaultDirections: SortDirectionMap = Object.keys(sortOptions).reduce(
      (directionMap, name) => {
        const defaultDirection = sortOptions[name]?.defaultDirection;

        if (defaultDirection) {
          directionMap[name] = defaultDirection;
        }

        return directionMap;
      },
      {}
    );

    return (
      <div className={classNames(css.sort, className)}>
        <Button
          data-sumo-test={`${dataSumoTestPrefix}-sort-btn`}
          endIcon={showEndIcon && <AngleDownIcon />}
          onClick={bind(this.toggleSortMenu, this)}
          startIcon={<SvgIcon aria-hidden component={SortIcon} />}
          sx={{
            pl: 0,
            pr: 0,
            whiteSpace: 'nowrap',
            textTransform: 'initial',
          }}
          variant='text'
          color='secondary'
          {...buttonProps}
        >
          <Typography
            className={classNames(css.sortText)}
            display='inline'
            whiteSpace='nowrap'
            color='inherit'
            {...buttonTextProps}
          >
            {`${
              sortOptions[currentProp]?.label
            } (${current.direction?.toLowerCase()}.)`}
          </Typography>
        </Button>
        <Menu
          anchorEl={sortMenuAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          onClose={bind(this.onCloseSortMenu, this)}
          open={sortMenuVisible}
          style={menuStyleProps ?? {}}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Sort
            defaultDirections={defaultDirections}
            onSortChange={bind(this.onSortChange, this)}
            sorts={sorts}
          >
            <FeatureFlags>
              {(featureFlags) => (
                <MenuList
                  data-sumo-test={`${dataSumoTestPrefix}-sort-menu-list`}
                >
                  {Object.entries(sortOptions)
                    .filter(
                      ([_name, option]) =>
                        !option.isEnabled || option.isEnabled(featureFlags)
                    )
                    .map(([name, option]) => (
                      <MenuItem key={name} selected={currentProp === name}>
                        <ListItemText>
                          <SortHeader prop={name}>
                            <span
                              data-sumo-test={`${dataSumoTestPrefix}-sort-menu-item-${
                                currentProp === name ? 'active' : 'inactive'
                              }`}
                            >
                              {option.label}
                            </span>
                          </SortHeader>
                        </ListItemText>
                      </MenuItem>
                    ))}
                </MenuList>
              )}
            </FeatureFlags>
          </Sort>
        </Menu>
      </div>
    );
  }
}
